/**
 * Consent audit log utilities (AC-856)
 *
 * Stores a timestamped, append-only record of each consent decision in
 * localStorage so it can be retrieved if consent is challenged.
 *
 * No PII is stored — entries contain only a random record ID, a UTC
 * timestamp, and the decision itself.
 */

const CONSENT_LOG_KEY = 'webapp-consent-log';

export interface ConsentLogEntry {
    /** Random ID that uniquely identifies this consent record (no PII). */
    id: string;
    /** ISO 8601 UTC timestamp of when the decision was made. */
    timestamp: string;
    /** The consent decision made by the user. */
    decision: 'accepted' | 'rejected' | 'partial';
}

const generateId = (): string => {
    if (
        typeof crypto !== 'undefined' &&
        typeof crypto.randomUUID === 'function'
    ) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Appends a new consent decision to the local audit log and returns the
 * created entry. The write is best-effort — a storage failure (e.g. quota
 * exceeded) is swallowed so the caller's consent action is never blocked.
 */
export const appendConsentLog = (
    decision: ConsentLogEntry['decision'],
): ConsentLogEntry => {
    const entry: ConsentLogEntry = {
        id: generateId(),
        timestamp: new Date().toISOString(),
        decision,
    };

    try {
        const existing = getConsentLog();
        const capped = existing.length >= 50 ? existing.slice(-49) : existing;
        localStorage.setItem(
            CONSENT_LOG_KEY,
            JSON.stringify([...capped, entry]),
        );
    } catch {
        // Best-effort — storage failure must not block the consent action.
    }

    return entry;
};

/**
 * Returns all consent log entries, oldest first.
 * Returns an empty array when no log exists or if the stored value cannot
 * be parsed. The log is write-only — it is not read back into the consent
 * gate; consent is derived exclusively from cookies.
 */
export const getConsentLog = (): ConsentLogEntry[] => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = localStorage.getItem(CONSENT_LOG_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? (parsed as ConsentLogEntry[]) : [];
    } catch {
        return [];
    }
};

/**
 * Returns the most recent consent log entry, or null if no decision has
 * ever been recorded.
 */
export const getLatestConsentEntry = (): ConsentLogEntry | null => {
    const log = getConsentLog();
    return log.length > 0 ? log[log.length - 1] : null;
};
